import { Dialog } from '@servicetitan/anvil2';
import { useState } from 'react';
import { useBreakpoint } from '@servicetitan/financing-utils-shared';
import {
    DeliveryMethod,
    LoanApplicationSubmitSource2,
    SubmittingUser,
} from '@servicetitan/financing-api-client';
import { LogService } from '@servicetitan/log-service';
import { PrequalContainer } from './prequal-flow/PrequalContainer';
import { ExternalApplicationForm } from './ExternalApplicationForm';
import { useProviderConfig } from '@servicetitan/financing-workflow-shared';

interface OnCloseHint {
    exitApplication: boolean;
}
interface ContainerProps {
    onClose: (hint?: OnCloseHint) => void;
    postApplicationAction?: (id: string | null | undefined) => void;
    onApplicationSubmitted?: () => void;
    showTechnicianInstructions?: boolean;
    allowSendViaEmail?: boolean;
    submittingUser?: SubmittingUser;
    source?: LoanApplicationSubmitSource2;
    skipPlanSelection?: boolean;
}

export const Container = ({
    onClose,
    postApplicationAction,
    onApplicationSubmitted,
    showTechnicianInstructions = false,
    allowSendViaEmail = false,
    submittingUser,
    source = LoanApplicationSubmitSource2.OnlineEstimates,
    skipPlanSelection = false,
}: ContainerProps) => {
    // All hooks must be called unconditionally at the top level
    const [isOpen, setIsOpen] = useState(true);
    const [isSendLinkConfirmationOpen, setIsSendLinkConfirmationOpenState] = useState(false);
    const [sendLinkDeliveryMethod, setSendLinkDeliveryMethod] = useState<DeliveryMethod>();
    const [isOpenApplicationConfirmationOpen, setIsOpenApplicationConfirmationOpen] =
        useState(false);
    const screenSize = useBreakpoint();
    const providerConfig = useProviderConfig();

    const setIsSendLinkConfirmationOpen = (isOpen: boolean, deliveryMethod?: DeliveryMethod) => {
        setIsSendLinkConfirmationOpenState(isOpen);
        if (deliveryMethod) {
            setSendLinkDeliveryMethod(deliveryMethod);
        }
        // Showing the send-link confirmation means the link was sent — i.e. the application was submitted.
        if (isOpen) {
            onApplicationSubmitted?.();
        }
    };

    // If provider config indicates choice flow should be used, render PrequalContainer
    if (providerConfig?.usePrequalChoiceFlow) {
        return (
            <PrequalContainer
                onClose={() => onClose()}
                allowSendViaEmail={allowSendViaEmail}
                showTechnicianInstructions={showTechnicianInstructions}
                submittingUser={submittingUser}
                source={source}
                onOpenApplication={response => {
                    postApplicationAction?.(response?.id); // Todo: Best wish.
                    setIsOpenApplicationConfirmationOpen(true);
                }}
            />
        );
    }

    // Existing logic for external application form
    return (
        <LogService endpoint="/api/log" storageKey="LogStorage_ExternalApplicationForm">
            <Dialog
                open={isOpen}
                size="large"
                fullScreen={screenSize.isSm || screenSize.isXs}
                onClose={() => {
                    setIsOpen(false);
                    if (skipPlanSelection) {
                        onClose({ exitApplication: true });
                    } else {
                        onClose();
                    }
                }}
                disableCloseOnClickOutside
            >
                <ExternalApplicationForm
                    isSendLinkConfirmationOpen={isSendLinkConfirmationOpen}
                    sendLinkDeliveryMethod={sendLinkDeliveryMethod}
                    isOpenApplicationConfirmationOpen={isOpenApplicationConfirmationOpen}
                    onClose={onClose}
                    setIsOpen={setIsOpen}
                    showTechnicianInstructions={showTechnicianInstructions}
                    allowSendViaEmail={allowSendViaEmail}
                    submittingUser={submittingUser}
                    source={source}
                    setIsSendLinkConfirmationOpen={setIsSendLinkConfirmationOpen}
                    skipPlanSelection={skipPlanSelection}
                    onOpenApplication={response => {
                        onApplicationSubmitted?.();
                        postApplicationAction?.(response?.id);
                        setIsOpenApplicationConfirmationOpen(true);
                    }}
                />
            </Dialog>
        </LogService>
    );
};
